import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, MenuItem, Typography } from '@mui/material';
import { FormApi } from 'final-form';
import { TextField } from 'mui-rff';
import React, { useCallback, useEffect, useState } from 'react';
import { UpdateFolder } from 'Types/folder';

import { TextFieldList } from '../../../components/TextFieldList';
import { getProjectTypes } from '../../../services/dealGPT';
import type { ProjectWithLastScanned, UpdateProjectOptions } from '../../../Types/dealGPT';
import { IndexType, ProjectStatus } from '../../../Types/enums';
import { extractTeamsData } from '../../../util/teamsUtils';
import { setPageTitle } from '../../DealGPT/utils';
import ProjectActiveDates from '../../DealGPT/ViewModels/ProjectActiveDates';

const IntellioAdvantageForm = ({
  form,
  setIsLoading,
  currentProject,
}: {
  form: FormApi<UpdateFolder, Partial<UpdateFolder>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  currentProject: UpdateProjectOptions;
}): JSX.Element => {
  const [selectedIndexToUse, setSelectedIndexToUse] = useState<string>(currentProject.indexToUse);
  const [projectTypes, setProjectTypes] = useState<string[]>();

  // Update Page title with current project name
  useEffect(() => {
    if (currentProject.name && currentProject.id) {
      setPageTitle({ projectId: currentProject.id, projectName: currentProject.name });
    }
  }, [currentProject.id, currentProject.name]);

  useEffect(() => {
    const fetchProjectTypes = async () => {
      setIsLoading(true);
      setProjectTypes(await getProjectTypes());
      setIsLoading(false);
    };

    fetchProjectTypes();
  }, [currentProject, setIsLoading]);

  const onTeamsLinkChange = useCallback(() => {
    const teamsLink = form.getFieldState('project.teamsLink')?.value;

    if (!teamsLink) return;

    const { teamsId, channelId } = extractTeamsData(teamsLink);

    // Update the form state
    form.change('project.teamsId', teamsId ?? '');
    form.change('project.channelId', channelId ?? '');
  }, [form]);

  const onActiveDatesChange = useCallback(
    (date: Date | undefined, field: 'activeStartDate' | 'activeEndDate') => {
      form.change(`project.${field}`, date);
    },
    [form]
  );

  return (
    <>
      <TextField
        label="Client Name"
        name="project.clientName"
        helperText="ex: ABC"
        required
        InputLabelProps={{ shrink: true }}
        margin="normal"
      />

      <TextField label="Project Type" required name="project.projectType" select>
        {projectTypes ? (
          projectTypes.map((option) => (
            <MenuItem value={option} key={option}>
              {option}
            </MenuItem>
          ))
        ) : (
          <MenuItem></MenuItem>
        )}
      </TextField>
      <TextField label="Status" name="project.status" select margin="normal">
        {Object.values(ProjectStatus).map((option) => (
          <MenuItem value={option} key={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      <TextField label="Index Type" name="project.indexToUse" select margin="normal">
        {Object.values(IndexType).map((option) => (
          <MenuItem value={option} key={option} onClick={() => setSelectedIndexToUse(option)}>
            {option}
          </MenuItem>
        ))}
      </TextField>

      {[IndexType.OPENAI, IndexType.GRAPH_RAG].includes(selectedIndexToUse as IndexType) && (
        <TextField
          label="Sharepoint Folder URL"
          name="project.sharepointFolderURL"
          helperText="ex: https://westmonroepartners1.sharepoint.com/:f:/r/sites/XXX/Shared%20Documents/XXX/XXX?XXX"
          required={[IndexType.OPENAI, IndexType.GRAPH_RAG].includes(
            selectedIndexToUse as IndexType
          )}
          InputLabelProps={{ shrink: true }}
          margin="normal"
        />
      )}
      <TextField
        label="Teams Link"
        name="project.teamsLink"
        helperText="ex: https://teams.microsoft.com/l/channel/.../General?groupId=..."
        onBlur={onTeamsLinkChange}
        InputLabelProps={{ shrink: true }}
        margin="normal"
      />
      <TextField
        label="Teams Id"
        name="project.teamsId"
        required
        helperText="ex: 9ff7c6d1-1606-4a26-XXbc-7bd9b48fXXXX"
        InputLabelProps={{ shrink: true }}
        margin="normal"
      />
      <TextField
        label="Channel Id"
        name="project.channelId"
        helperText="ex: 19%3Acf9f54bapr2855771a147a1ae884XXXXf%40thread.tacv2"
        InputLabelProps={{ shrink: true }}
        margin="normal"
      />
      <TextField
        label="Opportunity Id"
        name="project.opportunityId"
        helperText="ex: 006Nu000002YnAgXXX"
        required
        InputLabelProps={{ shrink: true }}
        margin="normal"
      />
      <ProjectActiveDates
        project={currentProject as ProjectWithLastScanned}
        onStartDateChange={(date: Date | undefined) => onActiveDatesChange(date, 'activeStartDate')}
        onEndDateChange={(date: Date | undefined) => onActiveDatesChange(date, 'activeEndDate')}
        width="100%"
      />
      <Accordion sx={{ marginBottom: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="excluded-paths-content"
          id="excluded-paths-header"
        >
          <Typography>Indexing Excluded Paths</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextFieldList
            title="Excluded Folders"
            name="project.ignoredFolders"
            label="Excluded folder"
            placeholder="e.g. IgnoredFolder/"
          />
          <TextFieldList
            title="Excluded Files"
            name="project.ignoredFiles"
            label="Excluded file"
            placeholder="e.g. .ignored-file.txt"
          />
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default IntellioAdvantageForm;
